import { useCallback } from 'react';
import useWatchTime from 'src/hooks/useWatchTime';
import useKeyboardShortcuts from 'src/hooks/useKeyboardShortcuts';
import usePlayback from 'src/hooks/usePlayback';
import { EXTERNAL_EVENTS, KEYBOARD_SHORTCUTS } from 'src/interfaces/enums';
import useSubtitle from 'src/hooks/useSubtitle';
import useUI from 'src/hooks/useUI';
import useExternalEvents from 'src/hooks/useExternalEvents';
import useAutoplay from 'src/hooks/useAutoplay';
import ApplicationService from 'src/services/ApplicationService';
import { ICue } from 'src/interfaces/PlayerTemplate';
import { UserEventTriggerType } from 'src/interfaces/Datahub';
import { useCsatCreateEvent } from 'src/hooks/csat/useCsatCreateEvents';
import { useUiComponentsVisibility } from 'src/hooks/useUiComponentsVisibility';
import { UIComponentsVisibilityState } from 'src/store/reducers/uiComponentsVisibility';
import useConfigEvents from '../useConfigEvents';
import useSyncStoreEvents from '../useSyncStoreEvents';
import useTrackingEvents from '../useTrackingEvents';
import {
  BACKWARD_SHORTCUT_SECONDS,
  FORWARD_SHORTCUT_SECONDS,
  RAISE_VOLUME_PERCENT,
  REDUCE_VOLUME_PERCENT,
} from './constants';

const usePlayerEvents = () => {
  const { player } = ApplicationService;
  const { togglePlay, raiseVolumePercent, reduceVolumePercent } = usePlayback();
  const { toggleFullscreen } = useUI();
  const { forwardSeconds, backwardSeconds } = useWatchTime();
  const { toggleSubtitle, addCue, removeCue } = useSubtitle();
  const { createConfigListeners } = useConfigEvents();
  const { createSyncStoreListeners } = useSyncStoreEvents();
  const { createTrackingListeners } = useTrackingEvents();
  const { hideAutoplay } = useAutoplay();
  const { setPlayerTime } = useWatchTime();
  const { addExternalEventsToListener } = useExternalEvents();
  const { setComponentsVisibility } = useUiComponentsVisibility();

  useCsatCreateEvent();

  useKeyboardShortcuts({
    [KEYBOARD_SHORTCUTS.TOGGLE_FULLSCREEN]: () => {
      toggleFullscreen(UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.TOGGLE_PLAY]: () => {
      togglePlay(UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.TOGGLE_SUBTITLE]: () => {
      toggleSubtitle(UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.TOGGLE_PLAY_ALTERNATIVE]: () => {
      togglePlay(UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.FORWARD]: () => {
      forwardSeconds(FORWARD_SHORTCUT_SECONDS, UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.BACKWARD]: () => {
      backwardSeconds(BACKWARD_SHORTCUT_SECONDS, UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.RAISE_VOLUME]: () => {
      raiseVolumePercent(RAISE_VOLUME_PERCENT, UserEventTriggerType.SHORTCUT);
    },
    [KEYBOARD_SHORTCUTS.REDUCE_VOLUME]: () => {
      reduceVolumePercent(REDUCE_VOLUME_PERCENT, UserEventTriggerType.SHORTCUT);
    },
  });

  addExternalEventsToListener({
    [EXTERNAL_EVENTS.ADD_CUE]: (cue: ICue) => addCue(cue),
    [EXTERNAL_EVENTS.REMOVE_CUE]: (cue: ICue) => removeCue(cue),
    [EXTERNAL_EVENTS.SET_PLAYER_TIME]: (time: number) => setPlayerTime(time),
    [EXTERNAL_EVENTS.CHANGEVIDEO]: () => hideAutoplay(),
    [EXTERNAL_EVENTS.PLAY]: () => player?.play(),
    [EXTERNAL_EVENTS.PAUSE]: () => player?.pause(),
  });

  addExternalEventsToListener({
    [EXTERNAL_EVENTS.SET_COMPONENTS_VISIBILITY]: (components: UIComponentsVisibilityState) => {
      setComponentsVisibility(components);
    },
  });

  const createListeners = useCallback(() => {
    createSyncStoreListeners();
    createTrackingListeners();
    createConfigListeners();
  }, [createConfigListeners, createSyncStoreListeners, createTrackingListeners]);

  return { createListeners };
};

export default usePlayerEvents;
